import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const AIProfileAssistant = ({ onProfileUpdate }) => {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm your profile assistant. I can help you update your work schedule, add work locations, update your bio, or set happy hour specials. Just tell me what you'd like to change!"
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/bartender/ai-assistant`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { reply, actions_taken, updated_profile } = response.data;

      setMessages(prev => [...prev, { role: "assistant", content: reply }]);

      // If profile was updated, notify parent component
      if (actions_taken && actions_taken.length > 0 && updated_profile) {
        onProfileUpdate(updated_profile);
        toast.success("Profile updated successfully!");
      }
    } catch (error) {
      console.error("AI Assistant error:", error);
      const errorMsg = error.response?.data?.detail || "Sorry, I encountered an error. Please try again.";
      setMessages(prev => [...prev, { role: "assistant", content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button with Helper Text */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {!isOpen && (
          <div className="bg-black/80 backdrop-blur-sm text-white text-xs px-3 py-2 rounded-lg max-w-[200px] shadow-lg border border-primary/30">
            <Sparkles className="w-3 h-3 inline mr-1 text-primary" />
            Use AI to update your profile, schedule & more
          </div>
        )}
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className={`rounded-full w-14 h-14 shadow-lg ${
            isOpen 
              ? "bg-white/10 hover:bg-white/20" 
              : "bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
          }`}
          data-testid="ai-assistant-toggle"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <MessageCircle className="w-6 h-6 text-white" />
          )}
        </Button>
      </div>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[340px] max-h-[500px] bg-black/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-white/10 bg-gradient-to-r from-primary/20 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/30 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">AI Profile Assistant</h3>
                <p className="text-white/50 text-xs">Update your profile with ease</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[250px] max-h-[300px]">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                    msg.role === "user"
                      ? "bg-primary text-white rounded-br-md"
                      : "bg-white/10 text-white/90 rounded-bl-md"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white/10 px-4 py-2 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 text-white/50 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/10">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tell me what to update..."
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
                disabled={isLoading}
                data-testid="ai-assistant-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="bg-primary hover:bg-primary/80 px-3"
                data-testid="ai-assistant-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-white/30 text-[10px] mt-2 text-center">
              Try: "Add Monday 6pm-2am at The Blue Bar" or "Update my bio to..."
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIProfileAssistant;
